import { API } from '@/redux/config/index';
import { useSiteId } from '@/utils/hooks/useCustomerId';
import { raiseErrorException } from '../../utils/raiseErrorException';
import { StnAPI } from '../config/customHeadersApi';
import {
    setAllAssignedGPUOne,
    setAllCustomersGPUOne,
    setCustomerGPUOne,
    setGPUOneMaasLogs,
    setMachineDetails,
    setReducerError,
    setUnassignedGPUOne,
    start,
} from '../reducers/gpuOneReducer';

// Get all customers with GPUOne data
export const getCustomers = () => async dispatch => {
	try {
		dispatch(start());
		const response = await API.get('/gpuone/customers');
		dispatch(setAllCustomersGPUOne(response?.data));
	} catch (error) {
		raiseErrorException(error, dispatch, setReducerError, 'get');
	}
};

// Set customer's GPUOne prefix (POST to /gpuone/customers)
export const setCustomerPrefix = (prefixData, toggle) => async dispatch => {
	try {
		dispatch(start());
		const response = await API.post('/gpuone/customers', prefixData);
		dispatch(setCustomerGPUOne(response?.data));
		dispatch(getUnassignedMachines());
		if (toggle) {
			toggle();
		}
	} catch (error) {
		raiseErrorException(error, dispatch, setReducerError, 'post');
	}
};

// Get MAAS machines assigned to customer
// Uses filterCustomer=true query param and customer-id header
export const getAssignedMachines =
	(customerID, params = {}) =>
	async dispatch => {
		try {
			dispatch(start());

			const { sort, ...otherParams } = params;
			const queryParams = new URLSearchParams({
				...otherParams,
				filterCustomer: true,
			});

			// Add sort parameter if provided
			if (sort) {
				queryParams.append('sort', sort);
			}

			const response = await API.post(`/gpuone/maas/assigned?${queryParams}`);

			dispatch(setCustomerGPUOne(response?.data));
		} catch (error) {
			raiseErrorException(error, dispatch, setReducerError, 'post');
		}
	};

// Get unassigned MAAS machines
export const getUnassignedMachines =
	(params = {}) =>
	async dispatch => {
		try {
			dispatch(start());
			const {
				page = 1,
				perPage = 10,
				sortBy = '-last_updated',
				filters = {},
			} = params;
			const startRow = (page - 1) * perPage;

			// Prepare request body with filters
			const requestBody = {
				...filters,
			};

			const response = await API.post(
				`/gpuone/maas/unassigned?sort=${sortBy}&startAt=${startRow}&maxResults=${perPage}`,
				requestBody,
			);

			dispatch(
				setUnassignedGPUOne({
					machines: response?.data?.machines || [],
					total: response?.data?.total || 0,
					currentPage: page,
					perPage: perPage,
					sortBy: sortBy,
				}),
			);
		} catch (error) {
			raiseErrorException(error, dispatch, setReducerError, 'get');
		}
	};
export const getAllAssignedMachines =
	(params = {}) =>
	async dispatch => {
		try {
			dispatch(start());
			const {
				page = 1,
				perPage = 10,
				sortBy = '-last_updated',
				filters = {},
			} = params;
			const startRow = (page - 1) * perPage;

			// Prepare request body with filters
			const requestBody = {
				...filters,
			};

			const response = await API.post(
				`/gpuone/maas/assigned?sort=${sortBy}&startAt=${startRow}&maxResults=${perPage}`,
				requestBody,
			);

			dispatch(
				setAllAssignedGPUOne({
					machines: response?.data?.machines || [],
					total: response?.data?.total || 0,
					currentPage: page,
					perPage: perPage,
					sortBy: sortBy,
				}),
			);
		} catch (error) {
			raiseErrorException(error, dispatch, setReducerError, 'get');
		}
	};

// Get logs for MAAS machine
export const getMachineLogs = (systemSerial, params) => async dispatch => {
	try {
		dispatch(start());
		const response = await API.post(
			`/gpuone/maas/${systemSerial}/logs`,
			params,
		);
		dispatch(setGPUOneMaasLogs({ systemSerial, data: response?.data }));
	} catch (error) {
		raiseErrorException(error, dispatch, setReducerError, 'post');
	}
};

// Get full log content for a specific log
export const getLogContent =
	(logId, page = 1) =>
	async dispatch => {
		try {
			dispatch(start());
			const response = await API.get(
				`/gpuone/logs/${logId}/content?page=${page}`,
			);
			return response?.data;
		} catch (error) {
			raiseErrorException(error, dispatch, setReducerError, 'get');
			throw error;
		}
	};

// Get MAAS machine details
export const getMachineDetails = maasId => async dispatch => {
	try {
		const response = await API.get(`/gpuone/maas/${maasId}`);
		dispatch(setMachineDetails(response?.data));
	} catch (error) {
		raiseErrorException(error, dispatch, setReducerError, 'get');
		throw error;
	}
};

// Assign MAAS machine to customer
export const assignMachineToCustomer =
	(systemSerial, customerData) => async dispatch => {
		try {
			dispatch(start());
			const response = await API.post(
				`/gpuone/maas/${systemSerial}/customer`,
				customerData,
			);
			// Refresh both assigned and unassigned machines lists after assignment
			dispatch(getAllAssignedMachines());
			dispatch(getUnassignedMachines());
			return response?.data;
		} catch (error) {
			raiseErrorException(error, dispatch, setReducerError, 'post');
			throw error;
		}
	};

// Unassign MAAS machine from customer
export const unassignMachineFromCustomer = systemSerial => async dispatch => {
	try {
		dispatch(start());
		const response = await API.delete(`/gpuone/maas/${systemSerial}/customer`);
		// Refresh both assigned and unassigned machines lists after unassignment
		dispatch(getAllAssignedMachines());
		dispatch(getUnassignedMachines());
		return response?.data;
	} catch (error) {
		raiseErrorException(error, dispatch, setReducerError, 'delete');
		throw error;
	}
};

export const assignMachineToMultipleCustomers =
	(systemSerialList, customerID) => async dispatch => {
		try {
			dispatch(start());
			const API_URL = customerID ? StnAPI : API;
			const response = await API_URL.post(
				`/gpuone/maas/customer/list`,
				{
					system_serials: systemSerialList,
				},
				{
					headers: {
						'x-customer-id': customerID,
						'x-site-id': useSiteId(),
					},
				},
			);
			dispatch(getAllAssignedMachines());
			dispatch(getUnassignedMachines());
		} catch (error) {
			raiseErrorException(error, dispatch, setReducerError, 'post');
			throw error;
		}
	};

export const createHelpTicket =
	(system_serial, data, toggle) => async dispatch => {
		try {
			dispatch(start());
			const response = await API.post(
				`/gpuone/maas/${system_serial}/help`,
				data,
			);
			dispatch(getAllAssignedMachines());
			if (toggle) {
				toggle();
			}
			return response.data; // Return the response data which should contain the ticket key
		} catch (error) {
			raiseErrorException(error, dispatch, setReducerError, 'post');
			throw error;
		}
	};
